import { useCallback, useMemo, useState } from 'react';
import type { MessageInstance } from 'antd/es/message/interface';
import type { TFunction } from 'i18next';
import type { useWorkOrderMutations } from '~/app/hooks/maintenance-execution/useWorkOrderMutations';
import type {
  FollowUpTicketRef,
  KanbanTicketView,
  KanbanWorkOrderView,
  PendingAssignAction,
  PendingCompleteAction,
  PendingHoldAction,
  PendingMoveAction,
} from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import { isTicketMoveTargetStatus } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';

type WorkOrderMutations = ReturnType<typeof useWorkOrderMutations>;

export type TicketCompletionPayload = {
  ticketId: string;
  executionResult: string;
  summary?: string;
  technicianComment?: string;
  workPerformed?: string;
  recommendation?: string;
  confirmedSerialNumber?: string;
  confirmedAssetIdentifier?: string;
  pauseDurationMinutes?: number;
  arrivedAt?: Date;
  faultCategory?: string;
  faultDescription?: string;
};

export type UseWorkOrderKanbanDialogsArgs = {
  workOrders: KanbanWorkOrderView[];
  filteredWorkOrders: KanbanWorkOrderView[];
  mutations: WorkOrderMutations;
  message: MessageInstance;
  t: TFunction<'maintenance'>;
};

/**
 * Pending dialog state and confirm handlers (assign / hold / complete / move /
 * follow-up), plus selected WO and move-target options for those dialogs.
 */
export function useWorkOrderKanbanDialogs(args: UseWorkOrderKanbanDialogsArgs) {
  const { workOrders, filteredWorkOrders, mutations, message, t } = args;

  const [selectedWorkOrderId, setSelectedWorkOrderId] = useState<string | null>(
    null,
  );
  const [pendingAssign, setPendingAssign] =
    useState<PendingAssignAction | null>(null);
  const [pendingHold, setPendingHold] = useState<PendingHoldAction | null>(
    null,
  );
  const [pendingComplete, setPendingComplete] =
    useState<PendingCompleteAction | null>(null);
  const [pendingMove, setPendingMove] = useState<PendingMoveAction | null>(
    null,
  );
  const [followUpTickets, setFollowUpTickets] = useState<FollowUpTicketRef[]>(
    [],
  );

  const selectedWorkOrder = useMemo(
    () =>
      filteredWorkOrders.find((wo) => wo.id === selectedWorkOrderId) ??
      workOrders.find((wo) => wo.id === selectedWorkOrderId) ??
      null,
    [filteredWorkOrders, workOrders, selectedWorkOrderId],
  );

  const moveTargetOptions = useMemo(
    () =>
      workOrders
        .filter((wo) => isTicketMoveTargetStatus(wo.status))
        .map((wo) => ({
          value: wo.id,
          label: `${wo.code} — ${wo.siteName} (${wo.status})`,
          code: wo.code,
          siteId: wo.siteId,
          siteName: wo.siteName,
          disabled:
            pendingMove != null && wo.id === pendingMove.sourceWorkOrderId,
        })),
    [workOrders, pendingMove],
  );

  const confirmAssign = useCallback(
    (technicianId: string) => {
      if (!pendingAssign) return;
      mutations.assign.mutate(
        {
          workOrderId: pendingAssign.workOrderId,
          body: { technicianId },
        },
        {
          onSuccess: () => {
            message.success(t('workOrders.kanban.messages.assignSuccess'));
            setPendingAssign(null);
          },
          onError: () =>
            message.error(t('workOrders.kanban.messages.assignFailed')),
        },
      );
    },
    [message, mutations.assign, pendingAssign, t],
  );

  const confirmHold = useCallback(
    (reason: string) => {
      if (!pendingHold) return;
      mutations.hold.mutate(
        {
          workOrderId: pendingHold.workOrderId,
          body: { reason },
        },
        {
          onSuccess: () => {
            message.success(t('workOrders.kanban.messages.holdSuccess'));
            setPendingHold(null);
          },
          onError: () =>
            message.error(t('workOrders.kanban.messages.holdFailed')),
        },
      );
    },
    [message, mutations.hold, pendingHold, t],
  );

  const confirmComplete = useCallback(
    (completions: TicketCompletionPayload[]) => {
      if (!pendingComplete) return;
      mutations.complete.mutate(
        {
          workOrderId: pendingComplete.workOrderId,
          body: {
            ticketCompletions: completions.map((item) => ({
              ticketId: item.ticketId,
              executionResult: item.executionResult,
              summary: item.summary,
              technicianComment: item.technicianComment,
              workPerformed: item.workPerformed,
              recommendation: item.recommendation,
              confirmedSerialNumber: item.confirmedSerialNumber,
              confirmedAssetIdentifier: item.confirmedAssetIdentifier,
              pauseDurationMinutes: item.pauseDurationMinutes,
              arrivedAt: item.arrivedAt,
              faultCategory: item.faultCategory,
              faultDescription: item.faultDescription,
            })) as never,
          },
        },
        {
          onSuccess: () => {
            const followUps = completions
              .filter((item) =>
                ['COMPLETED_WITH_ISSUE', 'NOT_DONE', 'CANCELLED'].includes(
                  item.executionResult,
                ),
              )
              .map((item) => {
                const ticket = pendingComplete.tickets.find(
                  (tkt) => tkt.id === item.ticketId,
                );
                return {
                  ticketId: item.ticketId,
                  ticketCode: ticket?.code ?? item.ticketId,
                  sourceWorkOrderId: pendingComplete.workOrderId,
                };
              });
            setFollowUpTickets(followUps);
            setPendingComplete(null);
            message.success(t('workOrders.kanban.messages.completeSuccess'));
          },
          onError: () =>
            message.error(t('workOrders.kanban.messages.completeFailed')),
        },
      );
    },
    [message, mutations.complete, pendingComplete, t],
  );

  const confirmMoveFromPicker = useCallback(
    (
      payload:
        | { mode: 'existing'; targetWorkOrderId: string }
        | { mode: 'createNew' },
    ) => {
      if (!pendingMove) return;
      mutations.moveTicket.mutate(
        {
          sourceWorkOrderId: pendingMove.sourceWorkOrderId,
          ticketId: pendingMove.ticketId,
          body:
            payload.mode === 'createNew'
              ? { createNewWorkOrder: true }
              : { targetWorkOrderId: payload.targetWorkOrderId },
        },
        {
          onSuccess: () => {
            message.success(
              payload.mode === 'createNew'
                ? t('workOrders.kanban.messages.createNewSuccess')
                : t('workOrders.kanban.messages.moveSuccess'),
            );
            setPendingMove(null);
            setFollowUpTickets((prev) =>
              prev.filter((item) => item.ticketId !== pendingMove.ticketId),
            );
          },
          onError: () =>
            message.error(
              payload.mode === 'createNew'
                ? t('workOrders.kanban.messages.createNewFailed')
                : t('workOrders.kanban.messages.moveFailed'),
            ),
        },
      );
    },
    [message, mutations.moveTicket, pendingMove, t],
  );

  const openMoveForTicket = useCallback(
    (
      ticket: FollowUpTicketRef | KanbanTicketView,
      sourceWorkOrderId: string,
      options?: { createNewWorkOrder?: boolean },
    ) => {
      const source = workOrders.find((wo) => wo.id === sourceWorkOrderId);
      setPendingMove({
        sourceWorkOrderId,
        sourceWorkOrderCode: source?.code ?? sourceWorkOrderId.slice(0, 8),
        sourceSiteId: source?.siteId ?? null,
        sourceSiteName: source?.siteName ?? null,
        ticketId: 'ticketId' in ticket ? ticket.ticketId : ticket.id,
        ticketCode: 'ticketCode' in ticket ? ticket.ticketCode : ticket.code,
        createNewWorkOrder: options?.createNewWorkOrder === true,
      });
    },
    [workOrders],
  );

  const findTicket = useCallback(
    (workOrderId: string, ticketId: string) => {
      const wo = workOrders.find((item) => item.id === workOrderId);
      const ticket = wo?.tickets.find((item) => item.id === ticketId);
      return ticket ? { workOrder: wo!, ticket } : null;
    },
    [workOrders],
  );

  const openMoveTicketFromBoard = useCallback(
    (workOrderId: string, ticketId: string) => {
      const found = findTicket(workOrderId, ticketId);
      if (found) {
        openMoveForTicket(found.ticket, workOrderId);
      }
    },
    [findTicket, openMoveForTicket],
  );

  return {
    selectedWorkOrderId,
    setSelectedWorkOrderId,
    selectedWorkOrder,
    pendingAssign,
    setPendingAssign,
    pendingHold,
    setPendingHold,
    pendingComplete,
    setPendingComplete,
    pendingMove,
    setPendingMove,
    followUpTickets,
    setFollowUpTickets,
    moveTargetOptions,
    confirmAssign,
    confirmHold,
    confirmComplete,
    confirmMoveFromPicker,
    openMoveForTicket,
    openMoveTicketFromBoard,
    findTicket,
    isAssigning: mutations.assign.isPending,
    isHolding: mutations.hold.isPending,
    isCompleting: mutations.complete.isPending,
    isMoving: mutations.moveTicket.isPending,
  };
}
